<template>
  <div ref="host" class="global-sticky-scroll-host">
    <slot />
  </div>
  <div
    v-show="visible"
    ref="bar"
    class="global-sticky-hscroll"
    :style="barStyle"
    @scroll.passive="onBarScroll"
  >
    <div :style="{ width: contentWidth + 'px' }"></div>
  </div>
</template>

<script>
export default {
  name: "StickyHScroll",
  props: {
    footerSelector: {
      type: String,
      default: ".v-footer",
    },
    footerFallbackPx: {
      type: Number,
      default: 56,
    },
    hideNativeScrollbar: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    visible: false,
    contentWidth: 0,
    left: 0,
    width: 0,
    bottom: 0,
    _scrollEl: null,
    _syncing: false,
    _mo: null,
    _ro: null,
    _raf: null,
    _overlayMo: null,
  }),
  computed: {
    barStyle() {
      return {
        left: `${this.left}px`,
        width: `${this.width}px`,
        bottom: `${this.bottom}px`,
      };
    },
  },
  mounted() {
    this.init();
    window.addEventListener("scroll", this.onWindowScroll, { passive: true });
    window.addEventListener("resize", this.refresh);

    // Hide the sticky bar whenever a Vuetify overlay/dialog is open.
    // We observe <body> because Vuetify toggles overlay-related classes there.
    if (!this._overlayMo) {
      this._overlayMo = new MutationObserver(() => this.refresh());
      this._overlayMo.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.onWindowScroll);
    window.removeEventListener("resize", this.refresh);
    this.teardown();
  },
  methods: {
    isOverlayActive() {
      // Vuetify overlays/dialogs typically toggle this class on <body>.
      if (document.body?.classList?.contains("v-overlay-scroll-blocked")) {
        return true;
      }

      // Fallback: any active overlay in DOM (covers v-dialog, v-menu, etc).
      return Boolean(document.querySelector(".v-overlay.v-overlay--active"));
    },
    init() {
      const host = this.$refs.host;
      if (!host || !(host instanceof HTMLElement)) return;

      if (this.hideNativeScrollbar) {
        host.classList.add("global-sticky-scroll-host--hide-native");
      } else {
        host.classList.remove("global-sticky-scroll-host--hide-native");
      }

      // Observe DOM changes until the real Vuetify wrapper exists
      if (!this._mo) {
        this._mo = new MutationObserver(() => this.attachScrollEl());
        this._mo.observe(host, { childList: true, subtree: true });
      }

      // Observe size changes (drawer open/close, layout shifts)
      if (!this._ro && typeof ResizeObserver !== "undefined") {
        this._ro = new ResizeObserver(() => this.refresh());
        this._ro.observe(host);
      }

      this.attachScrollEl();
      this.refresh();
    },
    teardown() {
      if (this._scrollEl) {
        this._scrollEl.removeEventListener("scroll", this.onTableScroll);
      }
      this._scrollEl = null;

      if (this._mo) {
        this._mo.disconnect();
        this._mo = null;
      }
      if (this._ro) {
        this._ro.disconnect();
        this._ro = null;
      }
      if (this._overlayMo) {
        this._overlayMo.disconnect();
        this._overlayMo = null;
      }
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
      this._syncing = false;
    },
    attachScrollEl() {
      const host = this.$refs.host;
      if (!host || !(host instanceof HTMLElement)) return;

      const candidates = [
        ...host.querySelectorAll(
          ".v-table__wrapper, .v-data-table__wrapper, .v-data-table__wrapper__scroll",
        ),
      ].filter((el) => el instanceof HTMLElement);

      const best =
        candidates.find((el) => el.scrollWidth > el.clientWidth + 1) ||
        candidates[0] ||
        null;

      if (!best || best === this._scrollEl) return;

      if (this._scrollEl) {
        this._scrollEl.removeEventListener("scroll", this.onTableScroll);
      }
      this._scrollEl = best;
      this._scrollEl.addEventListener("scroll", this.onTableScroll, {
        passive: true,
      });
    },
    refresh() {
      const host = this.$refs.host;
      const el = this._scrollEl;
      if (!host || !(host instanceof HTMLElement) || !el) {
        // try again after render
        this.$nextTick(() => {
          this.attachScrollEl();
          this.refresh();
        });
        return;
      }

      const rect = host.getBoundingClientRect();
      this.left = Math.max(0, Math.round(rect.left));
      this.width = Math.max(0, Math.round(rect.width)) || window.innerWidth;

      const footer = document.querySelector(this.footerSelector);
      const footerHeight =
        footer && footer instanceof HTMLElement
          ? Math.round(footer.getBoundingClientRect().height)
          : 0;
      this.bottom = footerHeight > 0 ? footerHeight : this.footerFallbackPx;

      const innerTable = el.querySelector("table");
      const scrollWidth = Math.max(
        el.scrollWidth || 0,
        innerTable?.scrollWidth || 0,
      );
      const clientWidth = el.clientWidth || 0;
      const needs = scrollWidth > clientWidth + 1;

      this.visible = needs && !this.isOverlayActive();
      const fallbackWidth =
        clientWidth > 0 ? clientWidth * 2 : (this.width || 800) * 2;
      this.contentWidth = Math.max(
        scrollWidth,
        needs ? scrollWidth || fallbackWidth : clientWidth + 1,
      );

      if (needs) {
        const bar = this.$refs.bar;
        if (
          bar &&
          bar instanceof HTMLElement &&
          bar.scrollLeft !== el.scrollLeft
        ) {
          bar.scrollLeft = el.scrollLeft;
        }
      }
    },
    onTableScroll() {
      if (this._syncing) return;
      const el = this._scrollEl;
      const bar = this.$refs.bar;
      if (!el || !bar || !(bar instanceof HTMLElement)) return;

      this._syncing = true;
      bar.scrollLeft = el.scrollLeft;
      requestAnimationFrame(() => {
        this._syncing = false;
      });
    },
    onBarScroll() {
      if (this._syncing) return;
      const el = this._scrollEl;
      const bar = this.$refs.bar;
      if (!el || !bar || !(bar instanceof HTMLElement)) return;

      this._syncing = true;
      el.scrollLeft = bar.scrollLeft;
      requestAnimationFrame(() => {
        this._syncing = false;
      });
    },
    onWindowScroll() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this.refresh();
      });
    },
  },
};
</script>
